import React, { useState, useRef, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { HelpCircle, Bell } from "lucide-react";
import Sidebar from "./Sidebar";
import "../App.css";

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const profileRef = useRef(null);

  const user = JSON.parse(localStorage.getItem("user")) || {};

  const showSidebar =
    location.pathname !== "/login" &&
    location.pathname !== "/register";

  const pageTitles = {
    "/app": { title: "Sign Yourself", subtitle: "Upload your document to begin the secure signing process." },
    "/signdoc": { title: "Select Identity", subtitle: "Select the verification method for signing this document." },
    "/choosemode": { title: "Choose Mode", subtitle: "Select the cryptographic mode for signing your document." },
    "/advanced": { title: "Advanced Options", subtitle: "Configure advanced signing options for your document." },
    "/reviewsign": { title: "Review & Sign", subtitle: "Review your document and signing details before signing." },
    "/customize-signature":{ title: "Customize Signature", subtitle: "Choose how your signature will appear on the document."},
    "/success": { title: "Successfully Signed", subtitle: "Your document has been signed successfully." },
    "/request-signatures": { title: "Request Signatures", subtitle: "Upload your document and start the signature request process." },
    "/add-recipients": { title: "Add recipients", subtitle: "Add recipient and set their signing order and permissions." },
    "/set-verification": { title: "Set Verification", subtitle: "Choose how recipients will verify their identity." },
    "/review-and-send": { title: "Review And Send", subtitle: "Review your request details and send for signatures." },
    "/request-success": { title: "Request Success", subtitle: "Your signature request has been sent successfully." },
    "/request-status": { title: "Request Status Page", subtitle: "Track the status of your signature requests in real time." },
    "/verification": { title: "Verify Document", subtitle: "Upload your document to begin the secure verification process." },
    "/verify-trust": { title: "Verify Document", subtitle: "Upload your document to begin the secure verification process." },
    "/automatic-verification": { title: "Automatic Verification", subtitle: "Running automated checks on your document's integrity." },
    "/ai-tamper-detection": { title: "AI Tamper Detection", subtitle: "AI engine is analyzing your document for tampering." },
    "/policy-key-validation": { title: "Policy & Key Validation", subtitle: "Validating signature policy rules and cryptographic keys." },
    "/verification-result": { title: "Verification Result", subtitle: "Verification completed successfully." },
    "/verification-report": { title: "Verification Report", subtitle: "Detailed verification report for your record." },
    "/drive": { title: "Secure Drive", subtitle: "Store, manage, and share your files securely." },
    "/upload": { title: "Upload", subtitle: "Upload files to encrypted IPFS storage." },
    "/filedetails": { title: "FileDetails", subtitle: "" },
    "/fileversion": { title: "FileVersion", subtitle: "" },
    "/sharefile": { title: "ShareFile", subtitle: "" },
    "/activitylog": { title: "ActivityLog", subtitle: "" },
    "/choose-signing-method": { title: "Choose Signing Method", subtitle: "Select the method for signing your artifact." },
    "/AI-security": { title: "Security Analytics Dashboard", subtitle: "AI-powered insights and risk analysis for digital trust and document security." },
    "/Organization": { title: "Organization Dashboard", subtitle: "Manage your organization's users, roles, and permissions." },
    "/Compliance": { title: "Compliance Dashboard", subtitle: "Monitor and manage compliance with industry standards and regulations." },
    "/HSM-management": { title: "HSM Management Dashboard", subtitle: "Monitor and manage Hardware Security Modules." },
    "/reports": { title: "Reports & Analytics", subtitle: "Comprehensive insights into your digital trust and signing activities." },
    "/external-integration": { title: "External Integration" },
    "/settings/security-authentication": { title: "Security & Authentication", subtitle: "Manage your account security, MFA, sessions, and login preferences." },
    "/settings/digital-identity": { title: "Digital Identity", subtitle: "Manage your digital certificates and signing identities." },
    "/settings/certificates": { title: "Certificates", subtitle: "View, import, and manage your digital signing certificates." },
    "/settings/post-quantum": { title: "Post-Quantum (PQC)", subtitle: "Configure post-quantum algorithms and signature preferences." },
    "/settings/my-notifications": { title: "Notifications", subtitle: "Manage your notification preferences and delivery channels." },
    "/users-control":{title:"Users & Roles", subtitle:"Manage users, roles and permissions across your organization."},
    "/settings/organizations": { title: "Organizations", subtitle: "Manage and monitor all organizations on the platform." },
    "/settings/integrations": { title: "Integrations", subtitle: "Connect and manage third-party apps and services." },
    "/settings/audit-logs": { title: "Audit Logs", subtitle: "Track and review all user activity and system events."},
    "/settings/backup-recovery": { title: "Backup & Recovery", subtitle: "Manage backups and restore your data securely."},
    "/settings/blockchain-anchoring": { title: "Blockchain / IPFS", subtitle: "Manage immutable records, anchored hashes and distributed storage."},
    "/blockchain-anchoring": { title: "Blockchain / IPFS", subtitle: "Manage immutable records, anchored hashes and distributed storage."},
    "/blockchain-anchoring/ipfs-files": { title: "IPFS Files", subtitle: "View and manage files stored securely on IPFS with blockchain verification. "},
    "/blockchain-anchoring/anchored-records": { title: "Anchored Records", subtitle: "View and manage all records anchored to blockchain and stored on IPFS for immutable proof and verification.."},
    "/blockchain-anchoring/verifications": { title: "Verifications", subtitle: "Verify digital signatures, certificates and records stored on blockchain/IPFS."},
    "/blockchain-anchoring/nodes": { title: "Nodes", subtitle: "Manage and monitor blockchain and IPFS nodes for a secure and decentralized infrasturcture."},
    "/blockchain-anchoring/smart-contracts": { title: "SmartContract", subtitle: "Manage and monitor smart contracts deployed on blockchain for secure and automated execution.."},
    "/blockchain-anchoring/settings": { title: "Settings", subtitle: " Configure your blockchain and IPFS network, security, and system preferences."},

    "/settings/system-preferences": { title: "System Preferences", subtitle: "Configure platform-wide preferences for display, security, and documents."},
    "/upload-artifact": { title: "Upload Artifact / Binary", subtitle: "Upload your artifact or binary to start the secure signing process." },
    "/generate-manifest":{title:"Generate Manifest", subtitle:"Review and generate a cryptographic manifest for your artifact."},
    "/signing-mode":{title:"Choose Signing Mode",subtitle:"Select the cryptographic signing mode for your artifact."},
    "/review-artifact": {title:"Review & Sign Artifact", subtitle:"Review all details before signing the artifact."},
    "/integration": {title:"CI/CD & OTA Integration", subtitle:"Integrate signed artifact into your CI/CD pipeline or prepare for OTA deployment."},
    "/complete": {title:"Artifact Signed Successfully!", subtitle:"Your artifact has been signed and is ready for distrubution or deployment."},
    "/add-user": { title: "Add New User", subtitle: "Create a new user account and assign roles and permissions." },
    "/trust-policies": { title: "Trust Policies", subtitle: "Define and manage trust policies for your organization." },
    "/trust-score": { title: "Trust Score", subtitle: "Real-time trust assessment across your organization's digital assets,identities, and operations." },
    "/timestamps": { title: "Timestamps", subtitle: "Create, manage and verify trusted timestamps for digital documents and artifacts." },
    "/timestamps/verification": { title: "Timestamp Verification", subtitle: "Verify the authenticity and integrity of a trusted timestamp." },
    "/timestamps/records": {title: "Timestamp Records", subtitle: "Trusted, verifiable and long-term proof of existence for digital records"}  };

  const pageInfo = pageTitles[location.pathname];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  let sidebarOffset = "0";
  if (showSidebar) {
    sidebarOffset = isCollapsed ? "72px" : "200px";
  }

  return (
    <div className="min-h-screen w-full flex background">
      {/* Sidebar */}
      {showSidebar && (
        <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
      )}

      {/* Right Side */}
      <div
        className="flex-1 flex flex-col"
        style={{
          marginLeft: sidebarOffset,
          transition: "margin-left 280ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Top Bar */}
        {showSidebar && (
          <div
  className="bg-white border-b border-gray-200 flex justify-between px-6"
  style={{
    height: "80px",
    alignItems: "center",
  }}
>

            {/* Left Section */}
            <div className="flex items-center gap-6 flex-1">
              {location.pathname === "/dashboard" ? (
                <input className="header-search" placeholder="Search documents, users, IDs..." />
              ) : (
                <div
  style={{
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    height: "100%",
  }}
>
  {pageInfo?.title && (
    <h1
      style={{
        fontSize: "24px",
        fontWeight: 700,
        color: "#1F2937",
        lineHeight: "1.2",
        margin: 0,
      }}
    >
      {pageInfo.title}
    </h1>
  )}

  {pageInfo?.subtitle && (
    <p
      style={{
        fontSize: "13px",
        color: "#64748B",
        margin: "2px 0 0 0",
        lineHeight: "1.3",
      }}
    >
      {pageInfo.subtitle}
    </p>
  )}
</div>
              )}
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-4">

              {/* Alerts */}
              <button className="header-icon">
                🔔
                <span>6</span>
              </button>

              {/* Notifications */}
              <button className="header-icon">
                <Bell size={20} />
                <span>12</span>
              </button>

              {/* Help */}
              <button className="header-icon" title="Help">
                <HelpCircle size={20} />
              </button>

              {/* Profile */}
              <div ref={profileRef} className="relative ml-1">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center gap-2 pl-2 pr-3 py-1 rounded-full hover:bg-gray-100"
                >
                  <div className="w-9 h-9 rounded-full bg-[#6610f2] text-white flex items-center justify-center font-semibold">
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                  <div className="text-left leading-tight">
                    <div className="text-sm font-semibold">{user?.name || "User"}</div>
                    <div className="text-xs text-gray-500 mt-0">{user?.role || "User"}</div>
                  </div>
                </button>

                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-lg shadow-lg border z-50">
                    <p className="px-4 py-2 text-xs text-gray-500 border-b">{user.email}</p>
                    <button
                      onClick={() => { navigate("/settings/my-profile"); setShowProfileMenu(false); }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100"
                    >
                      Profile
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className={`flex-1 overflow-y-auto overflow-x-hidden ${location.pathname === "/trust-score" ? "p-0" : "p-6"}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;